import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import Input from "components/Input";
import KeysContext from "contexts/KeysContext";
import ModalsManagerContext from "contexts/ModalsManager";
import * as openpgp from "openpgp";
import { useContext, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { generateSingleBinaryFromMultiplePublicKeys } from "utils/generateSingleBinaryFromMultiplePublicKeys";
import { getErrorMessage } from "utils/getErrorMessage";
import { getKeys } from "utils/getKeys";
import { getUserId } from "utils/getUserId";
import { ENotificationType, notification } from "utils/notification";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import { useNavigate } from "react-router-dom";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import Chip from "components/Chip";
import { logger } from "utils/logger";
import chatService from "api/chatService";
import ChatsContext from "contexts/ChatsContext";
import { IWorkspaceMember } from "dto/IWorkspace";
import SearchMemberNew from "components/SearchMemberNew";
import { convertToLetters } from "utils/convertToLetters";
import { Avatar } from "@mui/material";
import { useTranslation } from "react-i18next";

interface IErrorMessage {
  title: string;
  message: string;
  hint?: string;
}

const CreateChatModal: FC<Props> = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const { chats, setChats } = useContext(ChatsContext);

  const outSideRef = useRef(null);
  const [title, setTitle] = useState("");

  const [errorMesssage, setErrorMessage] = useState<IErrorMessage>(null);

  const [isLoading, setIsLoading] = useState(false);

  const { modals, setModals } = useContext(ModalsManagerContext);
  const { mutate: createChat } = chatService.useCreateChat();

  const [participants, setParticipants] = useState<IWorkspaceMember[]>([]);

  const isDisabled = () => {
    // if (!participants.length) {
    //   return true;
    // }

    return false;
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  const getCurrentUserPublicKey = async (): Promise<openpgp.key.Key> => {
    const myKeys = await getKeys(userId);

    const masterPublicKey = await readFileFromBase64(
      myKeys!.publicBase64Key,
      "application/other"
    );
    const armored = new TextDecoder().decode(masterPublicKey);

    const result = await openpgp.key.readArmored(armored);

    return result.keys[0];
  };

  const navigate = useNavigate();

  const handleSend = async () => {
    setIsLoading(true);

    await getCurrentUserPublicKey();
    const myKeys = await getKeys(userId);

    // Klucz obiektu dla przesyłki
    const parcelKey = await generateSingleBinaryFromMultiplePublicKeys([
      ...participants
        .map((entity) => entity.PublicKey ?? "")
        .filter((e) => !!e.length),
      myKeys!.publicBase64Key,
    ]);

    logger.log("Making request to BE");

    createChat(
      {
        PrivateKey: parcelKey.PrivateKey,
        PublicKey: parcelKey.PublicKey,
        Name: title,
        Participants: participants.map((p) => ({
          UserId: p.UserId as string,
        })),
      },
      {
        onSuccess: (response: any) => {
          setIsLoading(false);

          if (response?.data?.Id) {
            setChats([response.data, ...chats]);
            navigate(`/chat/${response.data.Id}`);
          }

          setModals({ ...modals, createChat: false });
        },
        onError(e: any) {
          setIsLoading(false);
          notification({
            title: t("errors.chatCreateFailed"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      }
    );
  };

  useContext(KeysContext);

  const refWrapper = useRef(null);

  const inputsRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef(null);

  const handleCloseModal = () => {
    setModals({ ...modals, hide: true });
  };

  const handleChooseMember = (member: IWorkspaceMember) => {
    const exists = participants.find((x) => x.Id == member.Id);

    if (!exists) {
      setParticipants([...participants, member]);
    }
  };

  return (
    <>
      <ModalLayout
        name="createChat"
        dock={DockPosition.DISABLED}
        off={true}
        customX={8}
        customY={0}
        ignore
        refWrapper={refWrapper}
        isAnimation
        fullHeight={true}
        widthAsLeftArea={true}
        disableMove={true}
      >
        <>
          <div className={styles.wrapper} ref={wrapperRef}>
            <div className={styles.title}>
              {t("navigation.chat")}
              <div className={styles.icons}>
                <CloseIcon
                  className={styles.closeIcon}
                  onClick={handleCloseModal}
                />
              </div>
            </div>
            <div className={styles.content}>
              <div className={styles.inputs}>
                <div ref={inputsRef} className={styles.inputsWrapper}>
                  <div className={styles.inputRow}>
                    <div
                      className={`${styles.inputControl} ${styles.dropdownParent}`}
                      ref={outSideRef}
                    >
                      <Input
                        label={t("common.name")}
                        value={title}
                        variant="filled"
                        onChange={(text) => setTitle(text)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.participants}>
                <SearchMemberNew
                  onChoose={handleChooseMember}
                  showMe={false}
                  label={t("chat.persons")}
                ></SearchMemberNew>
                <div className={styles.list}>
                  {participants.map((member) => (
                    <Chip
                      key={member.Id}
                      avatar={
                        <Avatar className={styles.avatar}>
                          {convertToLetters(member.Label)}
                        </Avatar>
                      }
                      element={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          {member.Label}
                        </div>
                      }
                      onDelete={() => {
                        setParticipants(
                          participants.filter((f) => f.Id !== member.Id)
                        );
                      }}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.footer}>
                <Button
                  loading={isLoading}
                  className={styles.btn2}
                  disabled={isDisabled()}
                  onClick={handleSend}
                  // text="Wyślij"
                  element={<>{t("common.create")}</>}
                />
              </div>
            </div>

            {errorMesssage && (
              <div className={styles.error}>
                <div className={styles.icons}>
                  <CloseIcon
                    className={styles.closeIcon}
                    onClick={handleCloseError}
                  />
                </div>
                <div className={styles.inner}>
                  <div className={styles.header}>{errorMesssage.title}</div>
                  <div className={styles.message}>{errorMesssage.message}</div>
                  <div className={styles.hint}>{errorMesssage.hint}</div>
                </div>
              </div>
            )}
          </div>
        </>
      </ModalLayout>
    </>
  );
};

export default CreateChatModal;
